import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import type {
  FileSource,
  WalkthroughClipResponse,
} from "@patent-studio/api-types";
import { refusalDetail } from "@/services/api-client";
import { clipKindFromSource } from "@/v2/figures/walkthrough-clips";
import { useUploadFile } from "./use-files";

/**
 * Store an unread walk-through clip and hang its id on the open figure.
 *
 * Uses the shared upload mutation so the task cache stays in the same shape as
 * every other file. Rows still going up sit beside the figures GET projection,
 * the same way a drawing still uploading sits beside the served list.
 */
export function useWalkthroughClipUpload(
  taskId: string | undefined,
  attach: (figureId: string, clipId: string) => void,
  stored: readonly WalkthroughClipResponse[] | undefined,
) {
  const upload = useUploadFile(taskId ?? "");
  const [pending, setPending] = useState<WalkthroughClipResponse[]>([]);

  const served = useMemo(
    () => new Set((stored ?? []).map((clip) => clip.file_id)),
    [stored],
  );

  // A clip the figures read has ever carried stops being pending. Read through
  // rather than synced into state: the row put up at upload time would
  // otherwise stand in front of the served one for a render before an effect
  // caught up.
  //
  // Remembered rather than read from `served` alone, because `stored` comes
  // from a query and can go empty again — a task switch, an eviction — and a
  // clip that has already been handed over must not come back as pending
  // because the list it was handed to is momentarily unavailable.
  const servedEverRef = useRef(new Set<string>());
  for (const fileId of served) servedEverRef.current.add(fileId);
  const visible = pending.filter(
    (clip) => !servedEverRef.current.has(clip.file_id),
  );

  const send = useCallback(
    async (
      figureId: string,
      file: File,
      source: FileSource = "direct_upload",
    ) => {
      if (taskId === undefined) return;
      try {
        const uploaded = await upload.mutateAsync({
          file,
          fileType: "walkthrough_clip",
          source,
        });
        // Dropping the now-served rows on the way in keeps the held list from
        // growing for as long as the screen stays open.
        setPending((held) => [
          ...held.filter((clip) => !servedEverRef.current.has(clip.file_id)),
          {
            file_id: uploaded.file_id,
            filename: uploaded.original_filename,
            kind: clipKindFromSource(source),
            status: uploaded.processing_status,
            transcript: null,
          },
        ]);
        attach(figureId, uploaded.file_id);
      } catch (err) {
        toast.error(
          refusalDetail(
            err,
            `Could not upload ${file.name}. Please try again.`,
          ),
        );
      }
    },
    [attach, taskId, upload],
  );

  return { send, pending: visible };
}
